import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PWAProvider } from './context/PWAContext';
import { Toaster } from './components/ui/sonner';
import { PWAInstallPrompt, PWAUpdateBanner, OfflineIndicator } from './components/PWAComponents';
import { canAccessRoles, PLATFORM_ADMIN_ROLES } from './lib/roleAccess';

// Pages are loaded only when their route is visited. This keeps the public entry
// point and each authenticated workspace from downloading unrelated modules.
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const PrelandsPage = lazy(() => import('./pages/PrelandsPageEnhanced'));
const OffersPage = lazy(() => import('./pages/OffersPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AnalyticsHubPage = lazy(() => import('./pages/AnalyticsHubPage'));
const VideoHubPage = lazy(() => import('./pages/VideoHubPage'));
const PodcastStudioPage = lazy(() => import('./pages/PodcastStudioPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'));
const SystemAnalyzerPage = lazy(() => import('./pages/SystemAnalyzerPage'));
const SupportCenterPage = lazy(() => import('./pages/SupportCenterPage'));
// Чат поддержки виден гостям на лендинге, поэтому живёт вне Layout — но
// грузится лениво, чтобы не тянуть markdown-рендер в публичный входной чанк.
const SupportChatWidget = lazy(() => import('./components/SupportChatWidget'));
const AutomationPage = lazy(() => import('./pages/AutomationPage'));
const CostsPage = lazy(() => import('./pages/CostsPage'));
const ProfilesPage = lazy(() => import('./pages/ProfilesPage'));
const SmartLinksPage = lazy(() => import('./pages/SmartLinksPage'));
const UnifiedTrafficPage = lazy(() => import('./pages/UnifiedTrafficPage'));
const WebhooksPage = lazy(() => import('./pages/WebhooksPage'));
const AIStudioPage = lazy(() => import('./pages/AIStudioPage'));
const SetupWizardPage = lazy(() => import('./pages/SetupWizardPage'));
const ThreadsAccountsPage = lazy(() => import('./pages/ThreadsAccountsPage'));
const ThreadsAutomationPage = lazy(() => import('./pages/ThreadsAutomationPage'));
const ThreadsFactoryPage = lazy(() => import('./pages/ThreadsFactoryPage'));
const InstagramFactoryPage = lazy(() => import('./pages/InstagramFactoryPage'));
const InstagramAccountsPage = lazy(() => import('./pages/InstagramAccountsPage'));
const ThreadsAnalyticsPage = lazy(() => import('./pages/ThreadsAnalyticsPage'));
const ThreadsMachinePage = lazy(() => import('./pages/ThreadsMachinePage'));
const ContentCalendarPage = lazy(() => import('./pages/ContentCalendarPage'));
const ContentStudioPage = lazy(() => import('./pages/ContentStudioPage'));
const MassSchedulerPage = lazy(() => import('./pages/MassSchedulerPage'));
const PostTemplatesPage = lazy(() => import('./pages/PostTemplatesPage'));
const HashtagManagerPage = lazy(() => import('./pages/HashtagManagerPage'));
const ABTestingPage = lazy(() => import('./pages/ABTestingPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
const CampaignDetailPage = lazy(() => import('./pages/CampaignDetailPage'));
const CRMDashboardPage = lazy(() => import('./pages/CRMDashboardPage'));
const ProxiesPage = lazy(() => import('./pages/ProxiesPage'));
const TrackingAnalyticsPage = lazy(() => import('./pages/TrackingAnalyticsPage'));
const TrackerIntegrationsPage = lazy(() => import('./pages/TrackerIntegrationsPage'));
const ContentRepackagingPage = lazy(() => import('./pages/ContentRepackagingPage'));
const AlertsConfigPage = lazy(() => import('./pages/AlertsConfigPage'));
const CRMWorkflowsPage = lazy(() => import('./pages/CRMWorkflowsPage'));
const LandingBuilderPage = lazy(() => import('./pages/LandingBuilderPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const VariablesHandbookPage = lazy(() => import('./pages/VariablesHandbookPage'));
const LinkInBioPage = lazy(() => import('./pages/LinkInBioPage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const WizardsPage = lazy(() => import('./pages/WizardsPage'));
const SmartLinksWizardPage = lazy(() => import('./pages/SmartLinksWizardPage'));
const ContentPlanWizardPage = lazy(() => import('./pages/ContentPlanWizardPage'));
const TrafficFunnelWizardPage = lazy(() => import('./pages/TrafficFunnelWizardPage'));
const GoalsPage = lazy(() => import('./pages/GoalsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const WorkspacesPage = lazy(() => import('./pages/WorkspacesPage'));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const EmailAccountsPage = lazy(() => import('./pages/EmailAccountsPage'));
const UrlAnalysisPage = lazy(() => import('./pages/UrlAnalysisPage'));
const CloakingPage = lazy(() => import('./pages/CloakingPage'));
const DoorwaysPage = lazy(() => import('./pages/DoorwaysPage'));
const TrafficSourcesPage = lazy(() => import('./pages/TrafficSourcesPage'));
const TrafficAnalyticsDashboard = lazy(() => import('./pages/TrafficAnalyticsDashboard'));
const SERPScraperPage = lazy(() => import('./pages/SERPScraperPage'));
const AdvancedAnalyticsPage = lazy(() => import('./pages/AdvancedAnalyticsPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const TDSRulesPage = lazy(() => import('./pages/TDSRulesPage'));
const QuizBuilderPage = lazy(() => import('./pages/QuizBuilderPage'));
const CRMAutomationPage = lazy(() => import('./pages/CRMAutomationPage'));
const WebPushPage = lazy(() => import('./pages/WebPushPage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const TelegramBotPage = lazy(() => import('./pages/TelegramBotPage'));
const TelegramFunnelEditorPage = lazy(() => import('./pages/TelegramFunnelEditorPage'));
const AutoRulesPage = lazy(() => import('./pages/AutoRulesPage'));
const BIDashboardPage = lazy(() => import('./pages/BIDashboardPage'));
const EventsTrackingPage = lazy(() => import('./pages/EventsTrackingPage'));
const ESPPage = lazy(() => import('./pages/ESPPage'));
const InfluencerPortalPage = lazy(() => import('./pages/InfluencerPortalPage'));
const PostbackValidationPage = lazy(() => import('./pages/PostbackValidationPage'));
const SEOContentPage = lazy(() => import('./pages/SEOContentPage'));
const MobilePushPage = lazy(() => import('./pages/MobilePushPage'));
const EngagementCenterPage = lazy(() => import('./pages/EngagementCenterPage'));

// New UBT Full modules
const VideoProcessingPage = lazy(() => import('./pages/VideoProcessingPage'));
const VideoStudioPage = lazy(() => import('./pages/VideoStudioPage'));
const StreamClipperPage = lazy(() => import('./pages/StreamClipperPage'));
const TrafficForgeClipperPage = lazy(() => import('./pages/OpusClipperPage'));
const SemanticSEOPage = lazy(() => import('./pages/SemanticSEOPage'));
const DynamicOffersPage = lazy(() => import('./pages/DynamicOffersPage'));
const QuestionParserPage = lazy(() => import('./pages/QuestionParserPage'));
const LeadMagnetsPage = lazy(() => import('./pages/LeadMagnetsPage'));
const MiniGamesPage = lazy(() => import('./pages/MiniGamesPage'));
const RedditTrafficPage = lazy(() => import('./pages/RedditTrafficPage'));
const PayoutIntegrationsPage = lazy(() => import('./pages/PayoutIntegrationsPage'));
const PlatformAppsPage = lazy(() => import('./pages/PlatformAppsPage'));
const ProductionBridgePage = lazy(() => import('./pages/ProductionBridgePage'));
const PWASettingsPage = lazy(() => import('./pages/PWASettingsPage'));
const GeoCompliancePage = lazy(() => import('./pages/GeoCompliancePage'));
const GeoPodsPage = lazy(() => import('./pages/GeoPodsPage'));
const ContentClustersPage = lazy(() => import('./pages/ContentClustersPage'));
const OfferCompliancePage = lazy(() => import('./pages/OfferCompliancePage'));
const CpaNetworksPage = lazy(() => import('./pages/CpaNetworksPage'));
const ContentPipelinePage = lazy(() => import('./pages/ContentPipelinePage'));
const GtmPlannerPage = lazy(() => import('./pages/GtmPlannerPage'));
const AioOptimizationPage = lazy(() => import('./pages/AioOptimizationPage'));
const DailyOpsPage = lazy(() => import('./pages/DailyOpsPage'));
const FTDPipelinePage = lazy(() => import('./pages/FTDPipelinePage'));

import './App.css';

export const RouteLoadingFallback = () => {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4" role="status" aria-live="polite">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-sky-500" aria-hidden="true" />
        <span>{t('app.loading', 'Загрузка интерфейса…')}</span>
      </div>
    </div>
  );
};

const PrivateRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return canAccessRoles(user.role, roles)
    ? children
    : <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="trafficforge-theme">
      <PWAProvider>
        <AuthProvider>
          <BrowserRouter>
          <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/* The password-reset email links here (backend auth_router reset_url). */}
          <Route path="/reset-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          
          {/* Public Link-in-Bio Profile page */}
          <Route path="/p/:slug" element={<PublicProfilePage />} />
          <Route path="/t/:slug" element={<PublicProfilePage />} />
          
          {/* PWA Share Target and File Handler */}
          <Route path="/share-target" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/open-file" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/protocol" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/campaigns" element={<PrivateRoute><CampaignsPage /></PrivateRoute>} />
          <Route path="/campaigns/:id" element={<PrivateRoute><CampaignDetailPage /></PrivateRoute>} />
          <Route path="/prelands" element={<PrivateRoute><PrelandsPage /></PrivateRoute>} />
          {/* Redirect old /quizzes to new /quiz-builder */}
          <Route path="/quizzes" element={<Navigate to="/quiz-builder" replace />} />
          <Route path="/offers" element={<PrivateRoute><OffersPage /></PrivateRoute>} />
          <Route path="/leads" element={<PrivateRoute><LeadsPage /></PrivateRoute>} />
          <Route path="/analytics" element={<PrivateRoute><AnalyticsPage /></PrivateRoute>} />
          <Route path="/analytics-hub" element={<PrivateRoute><AnalyticsHubPage /></PrivateRoute>} />
          <Route path="/video-hub" element={<PrivateRoute><VideoHubPage /></PrivateRoute>} />
          <Route path="/podcast-studio" element={<PrivateRoute><PodcastStudioPage /></PrivateRoute>} />
          <Route path="/profiles" element={<PrivateRoute><ProfilesPage /></PrivateRoute>} />
          <Route path="/smart-links" element={<PrivateRoute><SmartLinksPage /></PrivateRoute>} />
          <Route path="/link-in-bio" element={<PrivateRoute><LinkInBioPage /></PrivateRoute>} />
          <Route path="/webhooks" element={<PrivateRoute><WebhooksPage /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute roles={PLATFORM_ADMIN_ROLES}><UsersPage /></PrivateRoute>} />
          <Route path="/audit" element={<PrivateRoute roles={PLATFORM_ADMIN_ROLES}><AuditLogsPage /></PrivateRoute>} />
          <Route path="/system-analyzer" element={<PrivateRoute roles={PLATFORM_ADMIN_ROLES}><SystemAnalyzerPage /></PrivateRoute>} />
          <Route path="/support-center" element={<PrivateRoute roles={PLATFORM_ADMIN_ROLES}><SupportCenterPage /></PrivateRoute>} />
          <Route path="/automation" element={<PrivateRoute><AutomationPage /></PrivateRoute>} />
          <Route path="/costs" element={<PrivateRoute><CostsPage /></PrivateRoute>} />
          <Route path="/ai-studio" element={<PrivateRoute><AIStudioPage /></PrivateRoute>} />
          <Route path="/setup-wizard" element={<PrivateRoute><SetupWizardPage /></PrivateRoute>} />
          <Route path="/threads-accounts" element={<PrivateRoute><ThreadsAccountsPage /></PrivateRoute>} />
          <Route path="/threads-automation" element={<PrivateRoute><ThreadsAutomationPage /></PrivateRoute>} />
          <Route path="/engagement-center" element={<PrivateRoute><EngagementCenterPage /></PrivateRoute>} />
          <Route path="/threads-factory" element={<PrivateRoute><ThreadsFactoryPage /></PrivateRoute>} />
          <Route path="/instagram-factory" element={<PrivateRoute><InstagramFactoryPage /></PrivateRoute>} />
          <Route path="/email-accounts" element={<PrivateRoute><EmailAccountsPage /></PrivateRoute>} />
          <Route path="/threads-analytics" element={<PrivateRoute><ThreadsAnalyticsPage /></PrivateRoute>} />
          <Route path="/threads-machine" element={<PrivateRoute><ThreadsMachinePage /></PrivateRoute>} />
          <Route path="/content-calendar" element={<PrivateRoute><ContentCalendarPage /></PrivateRoute>} />
          <Route path="/mass-scheduler" element={<PrivateRoute><MassSchedulerPage /></PrivateRoute>} />
          <Route path="/post-templates" element={<PrivateRoute><PostTemplatesPage /></PrivateRoute>} />
          <Route path="/variables-handbook" element={<PrivateRoute><VariablesHandbookPage /></PrivateRoute>} />
          <Route path="/hashtag-manager" element={<PrivateRoute><HashtagManagerPage /></PrivateRoute>} />
          <Route path="/ab-testing" element={<PrivateRoute><ABTestingPage /></PrivateRoute>} />
          <Route path="/ab-tests" element={<PrivateRoute><ABTestingPage /></PrivateRoute>} />
          <Route path="/instagram" element={<PrivateRoute><InstagramAccountsPage /></PrivateRoute>} />
          <Route path="/traffic-streams" element={<PrivateRoute><UnifiedTrafficPage /></PrivateRoute>} />
          <Route path="/unified-traffic" element={<Navigate to="/traffic-streams" replace />} />
          <Route path="/smart-routing" element={<Navigate to="/traffic-streams" replace />} />
          <Route path="/notification-settings" element={<PrivateRoute><NotificationSettingsPage /></PrivateRoute>} />
          <Route path="/crm" element={<PrivateRoute><CRMDashboardPage /></PrivateRoute>} />
          <Route path="/proxies" element={<PrivateRoute><ProxiesPage /></PrivateRoute>} />
          <Route path="/tracking-analytics" element={<PrivateRoute><TrackingAnalyticsPage /></PrivateRoute>} />
          <Route path="/tracker-integrations" element={<PrivateRoute><TrackerIntegrationsPage /></PrivateRoute>} />
          <Route path="/content-repackaging" element={<PrivateRoute><ContentRepackagingPage /></PrivateRoute>} />
          <Route path="/alerts-config" element={<PrivateRoute><AlertsConfigPage /></PrivateRoute>} />
          <Route path="/crm-workflows" element={<PrivateRoute><CRMWorkflowsPage /></PrivateRoute>} />
          <Route path="/landing-builder" element={<PrivateRoute><LandingBuilderPage /></PrivateRoute>} />
          <Route path="/landing-builder/:id" element={<PrivateRoute><LandingBuilderPage /></PrivateRoute>} />
          <Route path="/admin-settings" element={<PrivateRoute roles={PLATFORM_ADMIN_ROLES}><AdminSettingsPage /></PrivateRoute>} />
          
          {/* Wizards */}
          <Route path="/wizards" element={<PrivateRoute><WizardsPage /></PrivateRoute>} />
          <Route path="/wizard/smart-links" element={<PrivateRoute><SmartLinksWizardPage /></PrivateRoute>} />
          <Route path="/wizard/content" element={<PrivateRoute><ContentPlanWizardPage /></PrivateRoute>} />
          <Route path="/wizard/traffic" element={<PrivateRoute><TrafficFunnelWizardPage /></PrivateRoute>} />
          
          {/* Goals & KPI */}
          <Route path="/goals" element={<PrivateRoute><GoalsPage /></PrivateRoute>} />
          
          {/* Reports */}
          <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
          
          {/* AI Assistant */}
          <Route path="/ai-assistant" element={<PrivateRoute><AIAssistantPage /></PrivateRoute>} />
          
          {/* Workspaces */}
          <Route path="/workspaces" element={<PrivateRoute><WorkspacesPage /></PrivateRoute>} />
          
          {/* API Keys & Integrations */}
          <Route path="/keys" element={<PrivateRoute><ApiKeysPage /></PrivateRoute>} />
          <Route path="/settings/keys" element={<PrivateRoute><ApiKeysPage /></PrivateRoute>} />
          <Route path="/integrations" element={<PrivateRoute><ApiKeysPage /></PrivateRoute>} />
          
          {/* URL Analysis */}
          <Route path="/url-analysis" element={<PrivateRoute><UrlAnalysisPage /></PrivateRoute>} />
          <Route path="/cloaking" element={<PrivateRoute><CloakingPage /></PrivateRoute>} />
          <Route path="/doorways" element={<PrivateRoute><DoorwaysPage /></PrivateRoute>} />
          <Route path="/traffic-sources" element={<PrivateRoute><TrafficSourcesPage /></PrivateRoute>} />
          <Route path="/traffic-analytics" element={<PrivateRoute><TrafficAnalyticsDashboard /></PrivateRoute>} />
          <Route path="/serp-scraper" element={<PrivateRoute><SERPScraperPage /></PrivateRoute>} />
          <Route path="/advanced-analytics" element={<PrivateRoute><AdvancedAnalyticsPage /></PrivateRoute>} />
          <Route path="/projects" element={<PrivateRoute><ProjectsPage /></PrivateRoute>} />
          <Route path="/tds-rules" element={<PrivateRoute><TDSRulesPage /></PrivateRoute>} />
          <Route path="/quiz-builder" element={<PrivateRoute><QuizBuilderPage /></PrivateRoute>} />
          <Route path="/crm-automation" element={<PrivateRoute><CRMAutomationPage /></PrivateRoute>} />
          <Route path="/webpush" element={<PrivateRoute><WebPushPage /></PrivateRoute>} />
          <Route path="/compliance" element={<PrivateRoute><CompliancePage /></PrivateRoute>} />
          <Route path="/geo-compliance" element={<PrivateRoute><GeoCompliancePage /></PrivateRoute>} />
          <Route path="/geo-pods" element={<PrivateRoute><GeoPodsPage /></PrivateRoute>} />
          <Route path="/content-clusters" element={<PrivateRoute><ContentClustersPage /></PrivateRoute>} />
          <Route path="/offer-compliance" element={<PrivateRoute><OfferCompliancePage /></PrivateRoute>} />
          <Route path="/cpa-networks" element={<PrivateRoute><CpaNetworksPage /></PrivateRoute>} />
          <Route path="/content-pipeline" element={<PrivateRoute><ContentPipelinePage /></PrivateRoute>} />
          <Route path="/gtm-planner" element={<PrivateRoute><GtmPlannerPage /></PrivateRoute>} />
          <Route path="/aio-optimization" element={<PrivateRoute><AioOptimizationPage /></PrivateRoute>} />
          <Route path="/daily-ops" element={<PrivateRoute><DailyOpsPage /></PrivateRoute>} />
          <Route path="/ftd-pipeline" element={<PrivateRoute><FTDPipelinePage /></PrivateRoute>} />
          <Route path="/partners" element={<PrivateRoute><PartnersPage /></PrivateRoute>} />
          <Route path="/telegram-bot" element={<PrivateRoute><TelegramBotPage /></PrivateRoute>} />
          <Route path="/telegram-bot/funnel/:funnelId" element={<PrivateRoute><TelegramFunnelEditorPage /></PrivateRoute>} />
          <Route path="/content-factory" element={<PrivateRoute><ContentStudioPage /></PrivateRoute>} />
          <Route path="/content-studio" element={<PrivateRoute><ContentStudioPage /></PrivateRoute>} />
          <Route path="/auto-rules" element={<PrivateRoute><AutoRulesPage /></PrivateRoute>} />
          <Route path="/bi-dashboard" element={<PrivateRoute><BIDashboardPage /></PrivateRoute>} />
          <Route path="/events-tracking" element={<PrivateRoute><EventsTrackingPage /></PrivateRoute>} />
          <Route path="/esp" element={<PrivateRoute><ESPPage /></PrivateRoute>} />
          <Route path="/influencer-portal" element={<PrivateRoute><InfluencerPortalPage /></PrivateRoute>} />
          <Route path="/postback-validation" element={<PrivateRoute><PostbackValidationPage /></PrivateRoute>} />
          <Route path="/seo-content" element={<PrivateRoute><SEOContentPage /></PrivateRoute>} />
          <Route path="/mobile-push" element={<PrivateRoute><MobilePushPage /></PrivateRoute>} />
          
          {/* New UBT Full modules */}
          <Route path="/video-processing" element={<PrivateRoute><VideoProcessingPage /></PrivateRoute>} />
          <Route path="/video-studio" element={<PrivateRoute><VideoStudioPage /></PrivateRoute>} />
          <Route path="/platform-scheduler" element={<PrivateRoute><ContentStudioPage /></PrivateRoute>} />
          <Route path="/stream-clipper" element={<PrivateRoute><StreamClipperPage /></PrivateRoute>} />
          <Route path="/opus-clipper" element={<PrivateRoute><TrafficForgeClipperPage /></PrivateRoute>} />
          <Route path="/semantic-seo" element={<PrivateRoute><SemanticSEOPage /></PrivateRoute>} />
          <Route path="/dynamic-offers" element={<PrivateRoute><DynamicOffersPage /></PrivateRoute>} />
          <Route path="/question-parser" element={<PrivateRoute><QuestionParserPage /></PrivateRoute>} />
          <Route path="/lead-magnets" element={<PrivateRoute><LeadMagnetsPage /></PrivateRoute>} />
          <Route path="/mini-games" element={<PrivateRoute><MiniGamesPage /></PrivateRoute>} />
          <Route path="/payout-integrations" element={<PrivateRoute><PayoutIntegrationsPage /></PrivateRoute>} />
          <Route path="/platform-apps" element={<PrivateRoute><PlatformAppsPage /></PrivateRoute>} />
          <Route path="/production-bridge" element={<PrivateRoute roles={PLATFORM_ADMIN_ROLES}><ProductionBridgePage /></PrivateRoute>} />
          <Route path="/pwa-settings" element={<PrivateRoute><PWASettingsPage /></PrivateRoute>} />
          <Route path="/app-settings" element={<PrivateRoute><PWASettingsPage /></PrivateRoute>} />
          <Route path="/reddit-traffic" element={<PrivateRoute><RedditTrafficPage /></PrivateRoute>} />
          
          {/* Redirects for common URL mistakes */}
          <Route path="/studio" element={<Navigate to="/ai-studio" replace />} />
          <Route path="/threads" element={<Navigate to="/threads-accounts" replace />} />
          <Route path="/calendar" element={<Navigate to="/content-calendar" replace />} />
          <Route path="/templates" element={<Navigate to="/post-templates" replace />} />
          <Route path="/hashtags" element={<Navigate to="/hashtag-manager" replace />} />
          <Route path="/landing" element={<Navigate to="/landing-builder" replace />} />
          <Route path="/landings" element={<Navigate to="/prelands" replace />} />
          <Route path="/trackers" element={<Navigate to="/tracker-integrations" replace />} />
          <Route path="/streams" element={<Navigate to="/traffic-streams" replace />} />
          <Route path="/analytics-threads" element={<Navigate to="/threads-analytics" replace />} />
          <Route path="/machine" element={<Navigate to="/threads-machine" replace />} />
          <Route path="/repackaging" element={<Navigate to="/content-repackaging" replace />} />
          
          {/* Catch-all redirect to dashboard for authenticated users */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        <Suspense fallback={null}>
          <SupportChatWidget />
        </Suspense>
        <Toaster />
        {/* PWA Components */}
        <PWAUpdateBanner />
        <PWAInstallPrompt />
        <OfflineIndicator />
        </BrowserRouter>
        </AuthProvider>
      </PWAProvider>
    </ThemeProvider>
  );
}

export default App;
